import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useParams } from "react-router-dom";

import { db2 } from "../firebaseConfig";

import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";

import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";

import AllergensTranslations from "./AllergensTraductions";
import BeverageProducts from "./BeverageProducts";

// ============================================================
// CONFIGURACIÓN VISUAL
// ============================================================

const THEME = {
  red: "#A64228",
  redDark: "#7D2E1D",
  redDeep: "#552116",

  cream: "#F4EEE4",
  creamLight: "#FBF8F2",
  paper: "#FFFDF9",

  beige: "#D8C8B3",
  beigeLight: "#E9DED0",
  beigeDark: "#B8A58D",

  text: "#28231F",
  textSoft: "#756A5F",
  textMuted: "#9A8E81",

  border: "#E5D9CA",

  green: "#4D7644",
  greenBg: "#EDF4E9",

  gold: "#92702B",
  goldBg: "#F7F0DD",
};

// ============================================================
// IDIOMAS
// ============================================================

const LANGUAGES = [
  {
    code: "es",
    label: "Español",
    flag: "/images/es.png",
  },
  {
    code: "cat",
    label: "Català",
    flag: "/images/cat.png",
  },
  {
    code: "en",
    label: "English",
    flag: "/images/en.png",
  },
  {
    code: "fr",
    label: "Français",
    flag: "/images/fr.png",
  },
];

// ============================================================
// CATEGORÍAS
// ============================================================

const CATEGORY_ORDER = ["Pinchos", "Tapas", "Postres"];

const CATEGORY_TRANSLATIONS = {
  es: {
    Pinchos: "Pinchos",
    Tapas: "Tapas",
    Postres: "Postres",
    Bebidas: "Bebidas",
  },

  cat: {
    Pinchos: "Pintxos",
    Tapas: "Tapes",
    Postres: "Postres",
    Bebidas: "Begudes",
  },

  en: {
    Pinchos: "Pinchos",
    Tapas: "Tapas",
    Postres: "Desserts",
    Bebidas: "Drinks",
  },

  fr: {
    Pinchos: "Brochettes",
    Tapas: "Tapas",
    Postres: "Desserts",
    Bebidas: "Boissons",
  },
};

// ============================================================
// ALÉRGENOS
// ============================================================

const ALLERGENS = [
  "gluten",
  "lacteos",
  "huevo",
  "pescado",
  "crustaceos",
  "frutosSecos",
  "altramuces",
  "cacahuete",
  "apio",
  "mostaza",
  "sesamo",
  "soja",
  "moluscos",
  "dioxidoAzufre",
];

// ============================================================
// HELPERS
// ============================================================

const getBrowserLanguage = () => {
  const browserLanguage = navigator.language || navigator.userLanguage || "es";

  const normalized = browserLanguage.toLowerCase();

  if (normalized.startsWith("ca")) return "cat";
  if (normalized.startsWith("es")) return "es";
  if (normalized.startsWith("fr")) return "fr";
  if (normalized.startsWith("en")) return "en";

  return "es";
};

const translateCategory = (category, language) => {
  return CATEGORY_TRANSLATIONS?.[language]?.[category] || category;
};

const translateAllergen = (allergen, language) => {
  return AllergensTranslations?.[language]?.[allergen] || allergen;
};

const getProductName = (product, translations, language) => {
  return (
    translations?.[product.codigo]?.[language]?.name ||
    product.ingrediente ||
    ""
  );
};

const getProductDescription = (product, translations, language) => {
  return (
    translations?.[product.codigo]?.[language]?.description ||
    product.comentarios ||
    ""
  );
};

const formatPrice = (value) => {
  const price = Number.parseFloat(value);

  if (!Number.isFinite(price)) {
    return "0.00";
  }

  return price.toFixed(2);
};

// ============================================================
// LANGUAGE SELECTOR
// ============================================================

const LanguageSelector = ({ language, onChange }) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
      }}
    >
      {LANGUAGES.map((item) => {
        const active = language === item.code;

        return (
          <Box
            key={item.code}
            component="button"
            type="button"
            onClick={() => onChange(item.code)}
            aria-label={item.label}
            title={item.label}
            sx={{
              appearance: "none",
              border: active
                ? "2px solid rgba(255,255,255,.95)"
                : "1px solid rgba(255,255,255,.28)",
              backgroundColor: active ? "#af0a0a" : "rgba(0,0,0,.16)",
              width: {
                xs: 30,
                sm: 33,
              },
              height: {
                xs: 30,
                sm: 33,
              },
              padding: active ? "4px" : "5px",
              borderRadius: "50%",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all .18s ease",
              "&:hover": {
                transform: "translateY(-1px)",
                backgroundColor: active ? "#fff" : "rgba(255,255,255,.15)",
              },
            }}
          >
            <Box
              component="img"
              src={item.flag}
              alt={item.label}
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

// ============================================================
// HERO
// ============================================================

const MenuHero = ({ language, onLanguageChange }) => {
  return (
    <Box
      component="header"
      sx={{
        position: "relative",
        minHeight: {
          xs: 185,
          sm: 200,
        },
        overflow: "hidden",

        backgroundImage: `
          linear-gradient(
            135deg,
            rgba(149, 28, 22, 0.85),
             rgba(149, 28, 22, 0.85)
          ),
          url("/images/esq.png")
        `,

        backgroundSize: "cover",
        backgroundPosition: "center",

        color: "#fff",

        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 50% 42%, rgba(255,255,255,.11), transparent 48%)",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 900,
          margin: "0 auto",
          px: {
            xs: 1.75,
            sm: 3,
          },
          pt: {
            xs: 1.5,
            sm: 2,
          },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            fontSize: {
              xs: 8,
              sm: 10,
            },
            letterSpacing: "3px",
            fontWeight: 900,
            opacity: 0.88,
          }}
        ></Typography>

        <LanguageSelector language={language} onChange={onLanguageChange} />
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 3,
        }}
      >
        <Box
          component="img"
          src="/images/logo-esq-header.png"
          alt="La Esquinita de Blai"
          sx={{
            width: {
              xs: "min(275px, 72vw)",
              sm: 365,
            },
            maxHeight: 120,
            objectFit: "contain",
            filter: "drop-shadow(0 6px 16px rgba(0,0,0,.25))",
          }}
        />

        <Box
          sx={{
            mt: 1.5,
            width: {
              xs: 225,
              sm: 290,
            },
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        ></Box>
      </Box>
    </Box>
  );
};

// ============================================================
// CATEGORY NAVIGATION
// ============================================================

const CategoryNavigation = ({
  categories,
  selectedCategory,
  onChange,
  language,
}) => {
  const items = [
    ...categories.map((category, index) => ({
      value: index,
      label: translateCategory(category, language),
    })),

    {
      value: "Bebidas",
      label: translateCategory("Bebidas", language),
    },
  ];

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 20,

        background: "rgba(244,238,228,.95)",
        backdropFilter: "blur(14px)",

        borderBottom: `1px solid ${THEME.border}`,

        boxShadow: "0 4px 18px rgba(50,30,20,.07)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "7px",

          overflowX: "auto",

          px: {
            xs: 1.25,
            sm: 2,
          },

          py: 1.15,

          maxWidth: 900,
          margin: "0 auto",

          scrollbarWidth: "none",

          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {items.map((item) => {
          const active = selectedCategory === item.value;

          return (
            <Box
              key={String(item.value)}
              component="button"
              type="button"
              onClick={() => onChange(item.value)}
              sx={{
                flexShrink: 0,

                border: active
                  ? `1px solid ${THEME.red}`
                  : `1px solid ${THEME.border}`,

                backgroundColor: active
                  ? "rgba(149, 28, 22, 0.95)"
                  : "rgba(255,255,255,.62)",

                color: active ? "#fff" : THEME.textSoft,

                borderRadius: "999px",

                px: {
                  xs: 1.8,
                  sm: 2.2,
                },

                py: 1,

                fontSize: {
                  xs: 10,
                  sm: 11,
                },

                lineHeight: 1,

                textTransform: "uppercase",

                letterSpacing: "1.15px",

                fontWeight: 900,

                cursor: "pointer",

                boxShadow: active ? "0 5px 14px rgba(166,66,40,.20)" : "none",

                transition: "all .18s ease",

                "&:hover": {
                  borderColor: THEME.red,
                  color: active ? "#fff" : THEME.red,
                },
              }}
            >
              {item.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ============================================================
// SECTION HEADER
// ============================================================

const SectionHeader = ({ category, language }) => {
  return (
    <Box
      sx={{
        textAlign: "center",

        px: 2,

        pt: {
          xs: 4,
          sm: 5,
        },

        pb: {
          xs: 2,
          sm: 2.5,
        },
      }}
    >
      <Typography
        component="h1"
        sx={{
          margin: 0,

          fontFamily: 'Georgia, "Times New Roman", serif',

          fontSize: {
            xs: 34,
            sm: 42,
            md: 46,
          },

          lineHeight: 1,

          fontWeight: 700,

          color: THEME.text,
        }}
      >
        {translateCategory(category, language)}
      </Typography>

      <Box
        sx={{
          mt: 1.5,

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          gap: 1,
        }}
      >
        <Box
          sx={{
            width: {
              xs: 42,
              sm: 55,
            },
            height: "1px",
            backgroundColor: THEME.beigeDark,
          }}
        />

        <Typography
          sx={{
            color: THEME.red,
            fontSize: 12,
            lineHeight: 1,
          }}
        >
          ✦
        </Typography>

        <Box
          sx={{
            width: {
              xs: 42,
              sm: 55,
            },
            height: "1px",
            backgroundColor: THEME.beigeDark,
          }}
        />
      </Box>
    </Box>
  );
};

// ============================================================
// ALLERGEN FILTER
// ============================================================

const AllergenFilter = ({ language, selectedAllergens, onChange }) => {
  const label =
    language === "es"
      ? "Filtrar por alérgenos"
      : language === "cat"
        ? "Filtrar per al·lèrgens"
        : language === "fr"
          ? "Filtrer par allergènes"
          : "Filter by allergens";

  const allLabel =
    language === "es"
      ? "Todos"
      : language === "cat"
        ? "Tots"
        : language === "fr"
          ? "Tous"
          : "All";

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 520,
        mx: "auto",

        px: {
          xs: 1.5,
          sm: 2,
        },

        pb: 2.5,
      }}
    >
      <FormControl fullWidth size="small">
        <InputLabel
          sx={{
            color: THEME.textSoft,

            "&.Mui-focused": {
              color: THEME.red,
            },
          }}
        >
          {label}
        </InputLabel>

        <Select
          multiple
          value={selectedAllergens}
          onChange={onChange}
          label={label}
          renderValue={(selected) => {
            if (!selected.length) {
              return allLabel;
            }

            return selected
              .map((allergen) => translateAllergen(allergen, language))
              .join(", ");
          }}
          sx={{
            backgroundColor: THEME.paper,

            borderRadius: "12px",

            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: THEME.border,
            },

            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: THEME.red,
            },

            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: THEME.red,
              borderWidth: 1,
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                mt: 0.5,
                borderRadius: "14px",
                boxShadow: "0 12px 30px rgba(40,25,15,.15)",
              },
            },
          }}
        >
          {ALLERGENS.map((allergen) => {
            const selected = selectedAllergens.includes(allergen);

            return (
              <MenuItem
                key={allergen}
                value={allergen}
                sx={{
                  py: 0.9,
                  px: 1.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 38,
                  }}
                >
                  <Box
                    component="img"
                    src={`/images/${allergen}.png`}
                    alt=""
                    sx={{
                      width: 23,
                      height: 23,
                      objectFit: "contain",
                    }}
                  />
                </ListItemIcon>

                <ListItemText
                  primary={translateAllergen(allergen, language)}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: THEME.text,
                  }}
                />

                <Checkbox
                  checked={selected}
                  sx={{
                    color: THEME.beigeDark,

                    "&.Mui-checked": {
                      color: THEME.red,
                    },
                  }}
                />
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
};

// ============================================================
// DIET TAG
// ============================================================

const DietTag = ({ type, language }) => {
  const vegan = type === "vegan";

  const label = vegan
    ? language === "es"
      ? "Vegano"
      : language === "cat"
        ? "Vegà"
        : language === "fr"
          ? "Végan"
          : "Vegan"
    : language === "es"
      ? "Vegetariano"
      : language === "cat"
        ? "Vegetarià"
        : language === "fr"
          ? "Végétarien"
          : "Vegetarian";

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,

        px: 0.85,
        py: 0.45,

        borderRadius: "999px",

        backgroundColor: vegan ? THEME.greenBg : THEME.goldBg,

        color: vegan ? THEME.green : THEME.gold,

        fontSize: 8,
        fontWeight: 900,

        textTransform: "uppercase",

        letterSpacing: ".4px",
      }}
    >
      <Box
        component="img"
        src={vegan ? "/images/vegan.png" : "/images/vegetarian.png"}
        alt=""
        sx={{
          width: 14,
          height: 14,
          objectFit: "contain",
        }}
      />

      {label}
    </Box>
  );
};

// ============================================================
// ALLERGEN BADGE
// ============================================================

const AllergenBadge = ({ allergen, language }) => {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,

        px: 0.7,
        py: 0.4,

        borderRadius: "999px",

        backgroundColor: "#F5F0E8",

        border: "1px solid #E9DFD1",

        color: "#665D54",

        fontSize: 8.5,
        lineHeight: 1,
        fontWeight: 600,
      }}
    >
      <Box
        component="img"
        src={`/images/${allergen}.png`}
        alt=""
        sx={{
          width: 13,
          height: 13,
          objectFit: "contain",
        }}
      />

      {translateAllergen(allergen, language)}
    </Box>
  );
};

// ============================================================
// PRODUCT DETAILS
// ============================================================

const ProductDetails = ({
  product,
  language,
  translations,
  compact = false,
}) => {
  const name = getProductName(product, translations, language);

  const description = getProductDescription(product, translations, language);

  const allergensList = Array.isArray(product.alergenos)
    ? product.alergenos
    : [];

  const tracesList = Array.isArray(product.trazas)
    ? product.trazas.filter((trace) => !allergensList.includes(trace))
    : [];

  return (
    <Box>
      <Typography
        component="h2"
        sx={{
          margin: 0,

          fontFamily: 'Georgia, "Times New Roman", serif',

          fontSize: compact
            ? {
                xs: 19,
                sm: 21,
              }
            : {
                xs: 27,
                sm: 32,
              },

          lineHeight: 1.08,

          fontWeight: 700,

          color: THEME.text,
        }}
      >
        {name}
      </Typography>

      {description && (
        <Typography
          sx={{
            mt: compact ? 0.6 : 1,

            color: THEME.textSoft,

            fontSize: compact
              ? {
                  xs: 11.5,
                  sm: 12,
                }
              : {
                  xs: 13,
                  sm: 14,
                },

            lineHeight: 1.6,
          }}
        >
          {description}
        </Typography>
      )}

      {(product.isVegan || product.isVegetarian) && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.6,
            mt: compact ? 1 : 1.5,
          }}
        >
          {product.isVegan && <DietTag type="vegan" language={language} />}

          {product.isVegetarian && (
            <DietTag type="vegetarian" language={language} />
          )}
        </Box>
      )}

      {allergensList.length > 0 && (
        <Box
          sx={{
            mt: compact ? 1.2 : 2,

            pt: compact ? 1 : 1.4,

            borderTop: "1px solid #EEE5D9",
          }}
        >
          <Typography
            sx={{
              mb: 0.75,

              fontSize: 8,

              letterSpacing: "1.5px",

              fontWeight: 900,

              color: THEME.textMuted,
            }}
          >
            {language === "es"
              ? "ALÉRGENOS"
              : language === "cat"
                ? "AL·LÈRGENS"
                : language === "fr"
                  ? "ALLERGÈNES"
                  : "ALLERGENS"}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {allergensList.map((allergen) => (
              <AllergenBadge
                key={allergen}
                allergen={allergen}
                language={language}
              />
            ))}
          </Box>
        </Box>
      )}

      {tracesList.length > 0 && (
        <Box
          sx={{
            mt: 1,

            display: "flex",
            flexWrap: "wrap",
            gap: "4px",

            color: THEME.textMuted,

            fontSize: 8.5,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: "inherit",
              color: "inherit",
              fontWeight: 700,
            }}
          >
            {language === "es"
              ? "Trazas:"
              : language === "cat"
                ? "Traces:"
                : language === "fr"
                  ? "Traces :"
                  : "Traces:"}
          </Typography>

          {tracesList.map((trace, index) => (
            <Typography
              key={`${trace}-${index}`}
              component="span"
              sx={{
                fontSize: "inherit",
                color: "#81786D",
              }}
            >
              {translateAllergen(trace, language)}
              {index < tracesList.length - 1 ? "," : ""}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ============================================================
// PRODUCT MODAL
// ============================================================

const ProductModal = ({ product, language, translations, onClose }) => {
  if (!product) {
    return null;
  }

  const name = getProductName(product, translations, language);

  const price = formatPrice(product.precioVentaSugerido);

  const image = product.imagen || "/images/default-escandallo.jpg";

  return (
    <Dialog
      open={Boolean(product)}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{
        sx: {
          margin: {
            xs: 1.2,
            sm: 3,
          },

          width: {
            xs: "calc(100% - 24px)",
            sm: "100%",
          },

          maxHeight: {
            xs: "calc(100vh - 24px)",
            sm: "calc(100vh - 48px)",
          },

          borderRadius: {
            xs: "22px",
            sm: "26px",
          },

          overflow: "hidden",

          backgroundColor: THEME.paper,

          boxShadow: "0 24px 80px rgba(45,25,15,.30)",
        },
      }}
    >
      {/* IMAGEN GRANDE */}

      <Box
        sx={{
          position: "relative",

          width: "100%",

          height: {
            xs: "min(54vh, 390px)",
            sm: 430,
          },

          backgroundColor: "#DED4C8",
        }}
      >
        <Box
          component="img"
          src={image}
          alt={name}
          sx={{
            width: "100%",
            height: "100%",
            display: "block",
            objectFit: "cover",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,

            background:
              "linear-gradient(to bottom, rgba(0,0,0,.08), transparent 45%, rgba(0,0,0,.25))",

            pointerEvents: "none",
          }}
        />

        <IconButton
          onClick={onClose}
          aria-label="Cerrar"
          sx={{
            position: "absolute",

            top: {
              xs: 10,
              sm: 14,
            },

            right: {
              xs: 10,
              sm: 14,
            },

            width: 40,
            height: 40,

            color: "#fff",

            backgroundColor: "rgba(35,20,15,.62)",

            backdropFilter: "blur(8px)",

            "&:hover": {
              backgroundColor: "rgba(35,20,15,.78)",
            },
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: {
            xs: 2.2,
            sm: 3,
          },

          "&:last-child": {
            pb: {
              xs: 2.5,
              sm: 3.5,
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <ProductDetails
              product={product}
              language={language}
              translations={translations}
            />
          </Box>

          <Box
            sx={{
              flexShrink: 0,

              minWidth: {
                xs: 74,
                sm: 86,
              },

              height: {
                xs: 48,
                sm: 54,
              },

              px: 1.2,

              borderRadius: "13px",

              backgroundColor: THEME.red,

              color: "#fff",

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              boxShadow: "0 7px 18px rgba(166,66,40,.24)",
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: 18,
                  sm: 21,
                },

                fontWeight: 900,

                letterSpacing: "-.5px",
              }}
            >
              {price} €
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// PRODUCT ROW
// ============================================================

const ProductRow = ({ product, language, translations, onOpen }) => {
  const name = getProductName(product, translations, language);

  const description = getProductDescription(product, translations, language);

  const price = formatPrice(product.precioVentaSugerido);

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(product)}
      sx={{
        appearance: "none",

        width: "100%",

        border: 0,

        borderBottom: `1px solid ${THEME.border}`,

        background: "transparent",

        padding: 0,

        textAlign: "left",

        cursor: "pointer",

        display: "flex",

        alignItems: "center",

        gap: {
          xs: 1.4,
          sm: 1.8,
        },

        py: {
          xs: 1.45,
          sm: 1.7,
        },

        "&:first-of-type": {
          borderTop: `1px solid ${THEME.border}`,
        },

        "&:hover .product-image": {
          transform: "scale(1.045)",
        },

        "&:hover .product-arrow": {
          transform: "translateX(3px)",
          color: THEME.red,
        },
      }}
    >
      {/* IMAGEN */}

      <Box
        sx={{
          position: "relative",

          flexShrink: 0,

          width: {
            xs: 82,
            sm: 100,
          },

          height: {
            xs: 82,
            sm: 100,
          },

          borderRadius: {
            xs: "14px",
            sm: "16px",
          },

          overflow: "hidden",

          backgroundColor: "#DED4C8",

          boxShadow: "0 4px 13px rgba(62,38,24,.11)",
        }}
      >
        <Box
          className="product-image"
          component="img"
          src={product.imagen || "/images/default-escandallo.jpg"}
          alt={name}
          loading="lazy"
          sx={{
            width: "100%",
            height: "100%",

            display: "block",

            objectFit: "cover",

            transition: "transform .35s cubic-bezier(.2,.7,.2,1)",
          }}
        />

        <Box
          sx={{
            position: "absolute",

            right: 5,
            bottom: 5,

            width: 25,
            height: 25,

            borderRadius: "50%",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            color: "#fff",

            backgroundColor: "rgba(45,25,18,.70)",

            backdropFilter: "blur(5px)",
          }}
        >
          <ZoomInRoundedIcon
            sx={{
              fontSize: 15,
            }}
          />
        </Box>
      </Box>

      {/* INFORMACIÓN */}

      <Box
        sx={{
          flex: 1,

          minWidth: 0,

          display: "flex",
          flexDirection: "column",

          justifyContent: "center",
        }}
      >
        <Typography
          component="h2"
          sx={{
            margin: 0,

            fontFamily: 'Georgia, "Times New Roman", serif',

            fontSize: {
              xs: 18,
              sm: 20,
            },

            lineHeight: 1.12,

            fontWeight: 700,

            color: THEME.text,

            pr: 0.5,
          }}
        >
          {name}
        </Typography>

        {description && (
          <Typography
            sx={{
              mt: 0.5,

              color: THEME.textSoft,

              fontSize: {
                xs: 10.8,
                sm: 11.5,
              },

              lineHeight: 1.45,

              display: "-webkit-box",

              WebkitLineClamp: {
                xs: 2,
                sm: 2,
              },

              WebkitBoxOrient: "vertical",

              overflow: "hidden",

              pr: 0.5,
            }}
          >
            {description}
          </Typography>
        )}

        {(product.isVegan || product.isVegetarian) && (
          <Box
            sx={{
              display: "flex",

              flexWrap: "wrap",

              gap: 0.45,

              mt: 0.75,
            }}
          >
            {product.isVegan && <DietTag type="vegan" language={language} />}

            {product.isVegetarian && (
              <DietTag type="vegetarian" language={language} />
            )}
          </Box>
        )}
      </Box>

      {/* PRECIO + FLECHA */}

      <Box
        sx={{
          flexShrink: 0,

          display: "flex",
          flexDirection: "column",

          alignItems: "flex-end",

          justifyContent: "center",

          gap: 0.6,

          pl: 0.2,
        }}
      >
        <Typography
          sx={{
            color: THEME.red,

            fontSize: {
              xs: 14,
              sm: 16,
            },

            fontWeight: 900,

            whiteSpace: "nowrap",

            letterSpacing: "-.2px",
          }}
        >
          {price} €
        </Typography>

        <KeyboardArrowRightRoundedIcon
          className="product-arrow"
          sx={{
            fontSize: 19,

            color: THEME.beigeDark,

            transition: "all .2s ease",
          }}
        />
      </Box>
    </Box>
  );
};

// ============================================================
// PRODUCT LIST
// ============================================================

const ProductList = ({ products, language, translations, onOpen }) => {
  if (!products.length) {
    return (
      <Box
        sx={{
          textAlign: "center",

          py: {
            xs: 7,
            sm: 9,
          },

          px: 2,

          color: THEME.textSoft,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Georgia, "Times New Roman", serif',

            color: THEME.red,

            fontSize: 34,

            lineHeight: 1,

            mb: 1,
          }}
        >
          —
        </Typography>

        <Typography
          sx={{
            fontSize: 13,
          }}
        >
          {language === "es"
            ? "No hay productos disponibles."
            : language === "cat"
              ? "No hi ha productes disponibles."
              : language === "fr"
                ? "Aucun produit disponible."
                : "No products available."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",

        maxWidth: 760,

        mx: "auto",

        px: {
          xs: 1.5,
          sm: 2,
        },

        pb: {
          xs: 4,
          sm: 5,
        },
      }}
    >
      {products.map((product) => (
        <ProductRow
          key={product.codigo || product.id}
          product={product}
          language={language}
          translations={translations}
          onOpen={onOpen}
        />
      ))}
    </Box>
  );
};

// ============================================================
// FOOTER
// ============================================================

const MenuFooter = () => {
  return (
    <Box
      component="footer"
      sx={{
        mt: 3,

        backgroundColor: "rgba(149, 28, 22, 0.95)",

        color: "#fff",

        textAlign: "center",

        px: 2,
        pt: 5,
        pb: 6,
      }}
    >
      <Box
        component="img"
        src="/images/logo-esq-header.png"
        alt="La Esquinita de Blai"
        sx={{
          width: {
            xs: 175,
            sm: 195,
          },

          maxWidth: "70vw",

          objectFit: "contain",

          filter: "brightness(0) invert(1)",

          opacity: 0.95,
        }}
      />

      <Box
        sx={{
          width: 220,
          maxWidth: "70vw",

          mx: "auto",

          mt: 1.5,

          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      ></Box>

      <Typography
        sx={{
          mt: 1.2,

          fontSize: 8,

          letterSpacing: "2px",

          opacity: 0.58,
        }}
      >
        PINCHOS · TAPAS · BUEN ROLLO
      </Typography>
    </Box>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const EsquinitaMenu = () => {
  const { category } = useParams();

  const [language, setLanguage] = useState(() => {
    if (typeof window === "undefined") {
      return "es";
    }

    return localStorage.getItem("language") || getBrowserLanguage();
  });

  const [products, setProducts] = useState([]);

  const [translations, setTranslations] = useState({});

  const [categories, setCategories] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState(0);

  const [selectedAllergens, setSelectedAllergens] = useState([]);

  const [selectedProduct, setSelectedProduct] = useState(null);

  const [loading, setLoading] = useState(true);

  // ==========================================================
  // IDIOMA
  // ==========================================================

  const handleLanguageChange = useCallback((newLanguage) => {
    setLanguage(newLanguage);

    localStorage.setItem("language", newLanguage);
  }, []);

  // ==========================================================
  // TRADUCCIONES
  // ==========================================================

  useEffect(() => {
    let cancelled = false;

    const loadTranslations = async () => {
      try {
        const snapshot = await getDocs(collection(db2, "traducciones"));

        if (cancelled) {
          return;
        }

        const map = snapshot.docs.reduce((acc, doc) => {
          const data = doc.data();

          if (data.codigo) {
            acc[data.codigo] = data.traducciones || {};
          }

          return acc;
        }, {});

        setTranslations(map);
      } catch (error) {
        console.error("Error cargando traducciones:", error);

        if (!cancelled) {
          setTranslations({});
        }
      }
    };

    loadTranslations();

    return () => {
      cancelled = true;
    };
  }, []);

  // ==========================================================
  // PRODUCTOS
  // ==========================================================

  useEffect(() => {
    setLoading(true);

    const productsRef = collection(db2, "escandallos-esq");

    const productsQuery = query(
      productsRef,

      where("subfam", "in", ["Pinchos", "Tapas", "Postres"]),

      where("visible", "==", true),
    );

    const unsubscribe = onSnapshot(
      productsQuery,
      (snapshot) => {
        const nextProducts = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setProducts(nextProducts);

        const availableCategories = [
          ...new Set(
            nextProducts.map((product) => product.subfam).filter(Boolean),
          ),
        ].sort((a, b) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b));

        setCategories(availableCategories);

        setLoading(false);
      },
      (error) => {
        console.error("Error escuchando escandallos:", error);

        setProducts([]);
        setCategories([]);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  // ==========================================================
  // CATEGORÍA DESDE URL
  // ==========================================================

  useEffect(() => {
    if (!category) {
      return;
    }

    if (category === "Bebidas") {
      setSelectedCategory("Bebidas");

      return;
    }

    const categoryIndex = categories.findIndex(
      (item) => item.toLowerCase() === String(category).toLowerCase(),
    );

    if (categoryIndex >= 0) {
      setSelectedCategory(categoryIndex);
    }
  }, [category, categories]);

  // ==========================================================
  // CATEGORÍA ACTUAL
  // ==========================================================

  const currentCategory =
    selectedCategory === "Bebidas"
      ? "Bebidas"
      : categories[Number(selectedCategory)];

  // ==========================================================
  // PRODUCTOS VISIBLES
  // ==========================================================

  const visibleProducts = useMemo(() => {
    if (!currentCategory || currentCategory === "Bebidas") {
      return [];
    }

    const result = products.filter(
      (product) => product.subfam === currentCategory,
    );

    if (!selectedAllergens.length) {
      return result;
    }

    return result.filter((product) => {
      const productAllergens = Array.isArray(product.alergenos)
        ? product.alergenos
        : [];

      return selectedAllergens.every(
        (allergen) => !productAllergens.includes(allergen),
      );
    });
  }, [products, currentCategory, selectedAllergens]);

  // ==========================================================
  // CAMBIO CATEGORÍA
  // ==========================================================

  const handleCategoryChange = useCallback((value) => {
    setSelectedCategory(value);

    setSelectedAllergens([]);

    setSelectedProduct(null);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  // ==========================================================
  // CAMBIO ALÉRGENOS
  // ==========================================================

  const handleAllergensChange = useCallback((event) => {
    setSelectedAllergens(event.target.value);
  }, []);

  // ==========================================================
  // ABRIR PRODUCTO
  // ==========================================================

  const handleOpenProduct = useCallback((product) => {
    setSelectedProduct(product);
  }, []);

  // ==========================================================
  // CERRAR PRODUCTO
  // ==========================================================

  const handleCloseProduct = useCallback(() => {
    setSelectedProduct(null);
  }, []);

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",

          backgroundColor: THEME.cream,

          display: "flex",
          flexDirection: "column",

          alignItems: "center",
          justifyContent: "center",

          gap: 2,
        }}
      >
        <Box
          component="img"
          src="/images/logo-esq-header.png"
          alt="La Esquinita de Blai"
          sx={{
            width: {
              xs: 175,
              sm: 200,
            },

            maxWidth: "70vw",

            objectFit: "contain",
          }}
        />

        <CircularProgress
          size={28}
          thickness={4}
          sx={{
            color: THEME.red,
          }}
        />

        <Typography
          sx={{
            color: THEME.textSoft,

            fontSize: 10,

            textTransform: "uppercase",

            letterSpacing: "1.5px",

            fontWeight: 800,
          }}
        >
          {language === "es"
            ? "Cargando carta..."
            : language === "cat"
              ? "Carregant carta..."
              : language === "fr"
                ? "Chargement..."
                : "Loading menu..."}
        </Typography>
      </Box>
    );
  }

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <Box
      sx={{
        height: "100vh",
        overflowY: "scroll",
        backgroundColor: THEME.cream,

        color: THEME.text,

        overflowX: "hidden",

        "& *": {
          boxSizing: "border-box",
        },
      }}
    >
      <MenuHero language={language} onLanguageChange={handleLanguageChange} />

      <CategoryNavigation
        categories={categories}
        selectedCategory={selectedCategory}
        onChange={handleCategoryChange}
        language={language}
      />

      <Box
        component="main"
        sx={{
          width: "100%",

          paddingBottom: "80px !important",
        }}
      >
        {currentCategory && (
          <SectionHeader category={currentCategory} language={language} />
        )}

        {currentCategory !== "Bebidas" && currentCategory !== "Postres" && (
          <AllergenFilter
            language={language}
            selectedAllergens={selectedAllergens}
            onChange={handleAllergensChange}
          />
        )}

        {currentCategory === "Bebidas" ? (
          <Box
            sx={{
              width: "100%",

              px: {
                xs: 1,
                sm: 2,
              },

              maxWidth: 900,

              mx: "auto",

              pb: 5,
            }}
          >
            <BeverageProducts language={language} idRestaurante="esq" />
          </Box>
        ) : (
          <ProductList
            products={visibleProducts}
            language={language}
            translations={translations}
            onOpen={handleOpenProduct}
          />
        )}
      </Box>

      <MenuFooter />

      {/* ======================================================
          MODAL PRODUCTO
          ====================================================== */}

      <ProductModal
        product={selectedProduct}
        language={language}
        translations={translations}
        onClose={handleCloseProduct}
      />
    </Box>
  );
};

export default EsquinitaMenu;
